import { Chrome, Laptop, Smartphone, Terminal } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DeviceWorkerItem } from "@/lib/api";

// Capabilities a device worker can advertise today. Mac/Chrome connector
// schemas are now advertised by Owletto at poll time; these labels keep the
// Devices UI human-readable while the gateway uses the capability strings for
// routing and manifest validation.
export const CAPABILITY_LABELS: Record<string, string> = {
  screentime: "Screen Time",
  healthkit: "Apple Health",
  photos: "Photos",
  calendar: "Calendar",
  reminders: "Reminders",
  computer_use: "Computer use",
};

const PLATFORM_LABELS: Record<string, string> = {
  ios: "iPhone",
  macos: "Mac",
  "chrome-extension": "Chrome",
  // A headless worker (`lobu daemon` directly or in Docker). Formerly also
  // carried a dead `node` entry — `node` was never a real device platform (the
  // server allowlist is macos/ios/chrome-extension/headless).
  headless: "Worker",
};

export function inferredPlatform(device: DeviceWorkerItem): string | null {
  if (device.platform) return device.platform;
  if (/^ios-bridge-/i.test(device.worker_id)) return "ios";
  if (/^mac-bridge-/i.test(device.worker_id)) return "macos";
  return null;
}

function platformLabel(platform: string | null): string {
  return PLATFORM_LABELS[platform ?? ""] ?? platform ?? "Device";
}

export function deviceName(device: DeviceWorkerItem): string {
  const label = device.label?.trim();
  // Old smoke/dev registrations used implementation labels like "iOS bridge"
  // or generic placeholders like "Device"; show user-facing names instead.
  if (label && !/^(device|(ios|mac|lobu)?\s*bridge)$/i.test(label))
    return label;
  return `${platformLabel(inferredPlatform(device))} · ${device.id.slice(0, 8)}`;
}

export function deviceVersion(appVersion: string | null): string | null {
  const value = appVersion?.trim();
  if (!value) return null;
  return /^\d/.test(value) ? `v${value}` : value;
}

export function deviceSecondaryLabel(
  device: DeviceWorkerItem,
  version: string | null,
): string {
  const id = /^(ios|mac)-bridge-/i.test(device.worker_id)
    ? null
    : device.worker_id;
  return [version, id].filter(Boolean).join(" · ");
}

/**
 * Resolve a device from the user's device list. The connectors list links by
 * the surrogate `id` (the `device_worker_id` a connection pins to), while the
 * Chrome extension deep-links with the stable `worker_id` (its client-generated
 * UUID) via `#worker=<worker_id>`. The two never equal, so a lookup keyed on
 * only one strands every deep link of the other kind — match either.
 */
export function findDeviceById(
  devices: readonly DeviceWorkerItem[],
  deviceId: string,
): DeviceWorkerItem | undefined {
  return devices.find(
    (d) => d.id === deviceId || d.worker_id === deviceId,
  );
}

export function PlatformIcon({
  platform,
  className,
}: {
  platform: string | null;
  className?: string;
}) {
  if (platform === "ios") {
    return (
      <Smartphone className={cn("h-4 w-4 text-muted-foreground", className)} />
    );
  }
  if (platform === "headless") {
    return (
      <Terminal className={cn("h-4 w-4 text-muted-foreground", className)} />
    );
  }
  if (platform === "chrome-extension") {
    return (
      <Chrome className={cn("h-4 w-4 text-muted-foreground", className)} />
    );
  }
  return <Laptop className={cn("h-4 w-4 text-muted-foreground", className)} />;
}

export function DeviceStatusDot({
  online,
  className,
}: {
  online: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block h-2 w-2 shrink-0 rounded-full",
        online ? "bg-green-500" : "bg-muted-foreground/40",
        className,
      )}
    />
  );
}
